'use client'

import { useLivePreview } from '@payloadcms/live-preview-react'

import type {
  CmsMarketplaceFooter,
  CmsMarketplaceHeader,
} from '@/helpers/api/fetchPayload'
import {
  toAuctionNavData,
  toGlobalHeaderNavData,
  toSharedFooterData,
} from '@/helpers/cms/marketplaceNav'

import Footer from './Footer'
import Header from './Header'

/**
 * Depth of the root layout's global fetch. Neither chrome global has a relationship, so the live
 * merge has nothing to populate, but the pane's data keeps the same shape as the initial render.
 */
const LIVE_PREVIEW_DEPTH = 1

/**
 * The marketplace header inside cms-portal's Live Preview pane for the Global Header.
 *
 * Every edit in the admin form arrives as the whole global, mapped through the same pure mappers
 * the layout uses, so a support link or a nav label re-renders as the editor types. Only the
 * global named by the preview token subscribes; the footer stays the published one.
 */
export function HeaderLivePreview({
  initial,
  serverURL,
  hasHomeIntent,
}: {
  initial: CmsMarketplaceHeader
  /** cms-portal origin; its API is served under its basePath, hence `apiRoute`. */
  serverURL: string
  hasHomeIntent: boolean
}) {
  const { data } = useLivePreview<CmsMarketplaceHeader>({
    initialData: initial,
    serverURL,
    apiRoute: '/cms-portal/api',
    depth: LIVE_PREVIEW_DEPTH,
  })

  return (
    <Header
      hasHomeIntent={hasHomeIntent}
      navData={toGlobalHeaderNavData(data)}
      auctionNav={toAuctionNavData(data)}
    />
  )
}

/** The marketplace footer inside the Live Preview pane for the Global Footer; see above. */
export function FooterLivePreview({
  initial,
  serverURL,
}: {
  initial: CmsMarketplaceFooter
  serverURL: string
}) {
  const { data } = useLivePreview<CmsMarketplaceFooter>({
    initialData: initial,
    serverURL,
    apiRoute: '/cms-portal/api',
    depth: LIVE_PREVIEW_DEPTH,
  })

  return <Footer data={toSharedFooterData(data)} />
}
